import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FolderTree, Plus, Trash2, Edit3, ChevronRight, ChevronDown,
  Settings, RefreshCw, Loader2, AlertCircle, CheckCircle,
  Building2, FileText, Package, Layers, Briefcase, Target
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import EmptyState from './EmptyState';
import { WidgetErrorBoundary } from './ErrorBoundary';

// ==========================================
// LEVEL ICONS (configurable per level name)
// ==========================================
const LEVEL_ICONS = {
  'Portfolio': Building2,
  'Program': Briefcase,
  'Project': Package,
  'Work Package': Layers,
  'Activity': FileText,
  'Task': Target,
  'Theme': FolderTree,
  'Epic': Layers,
  'Feature': Package,
  'Story': FileText,
};

const getIconForLevel = (levelName) => {
  return LEVEL_ICONS[levelName] || FileText;
};

// ==========================================
// WBS CONFIG EDITOR MODAL
// ==========================================

const WBSConfigModal = ({ open, onClose, config, onSave }) => {
  const [levels, setLevels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config?.levels) {
      setLevels(config.levels.map(l => ({ ...l })));
    }
  }, [config]);

  const handleAddLevel = () => {
    const nextLevel = levels.length + 1;
    setLevels([...levels, {
      level: nextLevel,
      name: `Level ${nextLevel}`,
      code_prefix: `L${nextLevel}`,
      color: '#6366F1',
    }]);
  };

  const handleRemoveLevel = (index) => {
    if (levels.length <= 2) {
      toast.error('Minimum 2 levels required');
      return;
    }
    const newLevels = levels.filter((_, i) => i !== index);
    // Renumber levels
    newLevels.forEach((l, i) => l.level = i + 1);
    setLevels(newLevels);
  };

  const handleLevelChange = (index, field, value) => {
    const newLevels = [...levels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    setLevels(newLevels);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        levels,
        enforce_hierarchy: true,
        require_wbs_mapping: false, // Phase 1: Optional
      });
      toast.success('WBS configuration updated');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configure WBS Levels
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
          <p className="text-sm text-gray-500">
            Define the hierarchy levels for your Work Breakdown Structure. 
            Each level can have a custom name and code prefix.
          </p>

          <div className="space-y-3">
            {levels.map((level, index) => (
              <div 
                key={index} 
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                data-testid={`wbs-level-${index}`}
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white border text-sm font-medium text-gray-600">
                  {level.level}
                </span>
                
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Level Name</Label>
                    <Input
                      value={level.name}
                      onChange={(e) => handleLevelChange(index, 'name', e.target.value)}
                      placeholder="e.g., Portfolio"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Code Prefix</Label>
                    <Input
                      value={level.code_prefix}
                      onChange={(e) => handleLevelChange(index, 'code_prefix', e.target.value.toUpperCase())}
                      placeholder="e.g., PF"
                      maxLength={5}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={level.color || '#6366F1'}
                        onChange={(e) => handleLevelChange(index, 'color', e.target.value)}
                        className="w-9 h-9 rounded border cursor-pointer"
                      />
                      <Input
                        value={level.color || '#6366F1'}
                        onChange={(e) => handleLevelChange(index, 'color', e.target.value)}
                        placeholder="#6366F1"
                        className="h-9 flex-1"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleRemoveLevel(index)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  disabled={levels.length <= 2}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={handleAddLevel}
            className="w-full"
            disabled={levels.length >= 10}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Level {levels.length >= 10 && '(Max 10)'}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// CREATE/EDIT NODE MODAL
// ==========================================

const NodeEditorModal = ({ open, onClose, mode, parentNode, config, existingNode, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    level: 1,
    parent_id: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingNode) {
      setForm({
        name: existingNode.name || '',
        description: existingNode.description || '',
        level: existingNode.level,
        parent_id: existingNode.parent_id,
      });
    } else if (parentNode) {
      setForm({
        name: '',
        description: '',
        level: parentNode.level + 1,
        parent_id: parentNode.id,
      });
    } else {
      setForm({
        name: '',
        description: '',
        level: 1,
        parent_id: null,
      });
    }
  }, [existingNode, parentNode, open]);

  const currentLevelConfig = useMemo(() => {
    return config?.levels?.find(l => l.level === form.level) || {};
  }, [config, form.level]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await onSave(form, mode === 'edit' ? existingNode?.id : null);
      toast.success(mode === 'edit' ? 'Node updated' : 'Node created');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'edit' ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {mode === 'edit' ? 'Edit WBS Node' : `Create ${currentLevelConfig.name || 'Node'}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Level indicator */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <span 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: currentLevelConfig.color || '#6366F1' }}
            />
            <span className="text-sm font-medium">
              Level {form.level}: {currentLevelConfig.name || `Level ${form.level}`}
            </span>
            {parentNode && (
              <span className="text-sm text-gray-500">
                (under: {parentNode.name})
              </span>
            )}
          </div>

          <div>
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={`Enter ${currentLevelConfig.name || 'node'} name`}
              data-testid="wbs-node-name-input"
            />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
              className="w-full h-24 px-3 py-2 border rounded-lg text-sm resize-none"
              data-testid="wbs-node-description-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-wbs-node-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================
// WBS TREE NODE COMPONENT
// ==========================================

const WBSCoreTreeNode = ({
  node,
  depth,
  config,
  expandedIds,
  onToggle,
  onCreateChild,
  onEdit,
  onDelete,
  selectedNodeId,
  onSelect,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  
  // Handle legacy nodes with null level
  const nodeLevel = node.level || (depth + 1);
  const levelConfig = config?.levels?.find(l => l.level === nodeLevel) || {};
  const Icon = getIconForLevel(levelConfig.name || node.node_type);
  const canAddChild = nodeLevel < (config?.levels?.length || 6);
  const isLegacyNode = node.level === null;

  return (
    <div data-testid={`wbs-core-node-${node.id}`}>
      <div 
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        className={`group flex items-center gap-2 py-2 px-2 rounded-lg cursor-pointer transition-colors
          ${isSelected ? 'bg-purple-50 border-l-2 border-purple-500' : 'hover:bg-gray-50'}
          ${isLegacyNode ? 'opacity-75' : ''}`}
        onClick={() => onSelect(node)}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Level indicator */}
        <span 
          className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
          style={{ 
            backgroundColor: `${levelConfig.color || '#6366F1'}20`,
            color: levelConfig.color || '#6366F1'
          }}
        >
          <Icon className="w-3 h-3" />
          {levelConfig.name || node.node_type || 'Item'}
        </span>

        {/* Code */}
        {node.code && (
          <span className="text-xs font-mono text-gray-400">{node.code}</span>
        )}

        {/* Name */}
        <span className="flex-1 text-sm font-medium text-gray-800 truncate">
          {node.name}
        </span>

        {/* Legacy indicator */}
        {isLegacyNode && (
          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
            Legacy
          </span>
        )}

        {/* Children count */}
        {(node.children_count > 0 || (node.children && node.children.length > 0)) && (
          <span className="text-xs text-gray-400">
            {node.children_count || node.children?.length || 0} items
          </span>
        )}

        {/* Linked items */}
        {node.linked_items && Object.values(node.linked_items).some(v => v > 0) && (
          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">
            Linked
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canAddChild && (
            <button
              onClick={(e) => { e.stopPropagation(); onCreateChild(node); }}
              className="p-1.5 hover:bg-green-100 text-green-600 rounded"
              title="Add child"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(node); }}
            className="p-1.5 hover:bg-blue-100 text-blue-600 rounded"
            title="Edit"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(node); }}
            className="p-1.5 hover:bg-red-100 text-red-600 rounded"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <WBSCoreTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              config={config}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onCreateChild={onCreateChild}
              onEdit={onEdit}
              onDelete={onDelete}
              selectedNodeId={selectedNodeId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN WBS STRUCTURE MANAGER COMPONENT
// ==========================================

const WBSStructureManager = ({ onNodeSelect, showHeader = true }) => {
  const { getActiveTenantId } = useAuth();
  const activeTenantId = getActiveTenantId();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [tree, setTree] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Modal states
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  const [nodeModalMode, setNodeModalMode] = useState('create'); // 'create' | 'edit'
  const [parentNode, setParentNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Load WBS tree and config
  const loadData = useCallback(async () => {
    if (!activeTenantId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [configRes, treeRes] = await Promise.all([
        api.getWBSConfig(),
        api.getWBSCoreTree(),
      ]);
      
      setConfig(configRes.data);
      setTree(treeRes.data?.tree || []);
      
      // Auto-expand first level
      const firstLevelIds = new Set(treeRes.data?.tree?.map(n => n.id) || []);
      setExpandedIds(firstLevelIds);
    } catch (err) {
      console.error('Failed to load WBS data:', err);
      toast.error('Failed to load WBS structure');
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle expand/collapse
  const handleToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Expand all nodes
  const expandAll = useCallback(() => {
    const allIds = new Set();
    const collect = (nodes) => {
      for (const node of nodes) {
        allIds.add(node.id);
        if (node.children) collect(node.children);
      }
    };
    collect(tree);
    setExpandedIds(allIds);
  }, [tree]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // Create root node
  const handleCreateRoot = () => {
    setParentNode(null);
    setEditingNode(null);
    setNodeModalMode('create');
    setNodeModalOpen(true);
  };

  // Create child node
  const handleCreateChild = (parent) => {
    setParentNode(parent);
    setEditingNode(null);
    setNodeModalMode('create');
    setNodeModalOpen(true);
  };

  // Edit node
  const handleEdit = (node) => {
    setEditingNode(node);
    setParentNode(null);
    setNodeModalMode('edit');
    setNodeModalOpen(true);
  };

  // Delete node
  const handleDelete = async (node) => {
    setDeleteConfirm(node);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.deleteWBSCoreNode(deleteConfirm.id, deleteConfirm.children_count > 0);
      toast.success('Node deleted');
      loadData();
      if (selectedNode?.id === deleteConfirm.id) {
        setSelectedNode(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to delete node');
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Save node
  const handleSaveNode = async (form, nodeId) => {
    if (nodeId) {
      // Update
      await api.updateWBSCoreNode(nodeId, {
        name: form.name,
        description: form.description,
      });
    } else {
      // Create
      await api.createWBSCoreNode({
        name: form.name,
        description: form.description,
        level: form.level,
        parent_id: form.parent_id,
      });
    }
    loadData();
  };

  // Save config
  const handleSaveConfig = async (newConfig) => {
    await api.updateWBSConfig(newConfig);
    loadData();
  };

  // Node selection
  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    onNodeSelect?.(node);
  };

  // Stats
  const stats = useMemo(() => {
    let count = 0;
    const countNodes = (nodes) => {
      for (const node of nodes) {
        count++;
        if (node.children) countNodes(node.children);
      }
    };
    countNodes(tree);
    return { totalNodes: count, levels: config?.levels?.length || 0 };
  }, [tree, config]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!activeTenantId) {
    return (
      <EmptyState
        icon={FolderTree}
        title="No tenant selected"
        description="Please select a tenant to view WBS structure."
      />
    );
  }

  return (
    <WidgetErrorBoundary widgetName="WBS Structure Manager">
      <div className="flex flex-col h-full" data-testid="wbs-structure-manager">
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between mb-4 pb-4 border-b">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FolderTree className="w-6 h-6 text-purple-600" />
                Work Breakdown Structure
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {stats.totalNodes} nodes • {stats.levels} levels configured
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                Collapse All
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfigModalOpen(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Configure Levels
              </Button>
              <Button size="sm" onClick={handleCreateRoot} data-testid="create-root-wbs-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Root Node
              </Button>
              <Button variant="ghost" size="sm" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Levels Legend */}
        {config?.levels && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg flex-wrap">
            <span className="text-xs font-medium text-gray-500">Levels:</span>
            {config.levels.map(level => (
              <span
                key={level.level}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                style={{
                  backgroundColor: `${level.color}20`,
                  color: level.color,
                }}
              >
                {React.createElement(getIconForLevel(level.name), { className: 'w-3 h-3' })}
                {level.name} ({level.code_prefix})
              </span>
            ))}
          </div>
        )}

        {/* Tree */}
        <div className="flex-1 overflow-y-auto border rounded-lg bg-white">
          {tree.length === 0 ? (
            <EmptyState
              icon={FolderTree}
              title="No WBS structure defined"
              description="Start by creating root nodes for your work breakdown structure."
              action={
                <Button onClick={handleCreateRoot}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Node
                </Button>
              }
            />
          ) : (
            <div className="p-2">
              {tree.map(node => (
                <WBSCoreTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  config={config}
                  expandedIds={expandedIds}
                  onToggle={handleToggle}
                  onCreateChild={handleCreateChild}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  selectedNodeId={selectedNode?.id}
                  onSelect={handleNodeSelect}
                />
              ))}
            </div>
          )}
        </div>

        {/* Config Modal */}
        <WBSConfigModal
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          config={config}
          onSave={handleSaveConfig}
        />

        {/* Node Editor Modal */}
        <NodeEditorModal
          open={nodeModalOpen}
          onClose={() => setNodeModalOpen(false)}
          mode={nodeModalMode}
          parentNode={parentNode}
          config={config}
          existingNode={editingNode}
          onSave={handleSaveNode}
        />

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Delete WBS Node
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>"{deleteConfirm?.name}"</strong>?
              </p>
              {deleteConfirm?.children_count > 0 && (
                <p className="mt-2 text-amber-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  This node has {deleteConfirm.children_count} children that will also be deleted.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </WidgetErrorBoundary>
  );
};

export default WBSStructureManager;
